import { SCHEDULE_TYPES } from '@src/constants/commons';
import { FSCollections } from '@src/constants/database';
import type {
  ExecutionElasticIdSource,
  ExecutionElasticSummarySource,
  ExecutionSearchIdHit,
  ExecutionSearchIdsParams,
  ExecutionSearchIdsResult,
  ExecutionSearchSummariesParams,
  ExecutionSearchSummary,
} from '@src/models/interfaces/execution.interface';
import type { ElasticQueryParams } from '@src/models/interfaces/general/ElasticQueryParams.interface';
import type {
  ElasticSearchHit,
  ElasticSearchResponse,
} from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import { DateUtil } from '@src/packages/utils/dates';
import { buildPartialMatch } from '@src/util/elastic/buildPartialMatch';

const SUMMARY_PAGE_SIZE = 8;
const SUMMARY_FIELDS = [
  'id',
  'division.id',
  'division.name',
  'schedule.id',
  'schedule.name',
  'startAt',
];
const ID_FIELDS = ['id', 'divisionId'];

export class ExecutionMapper {
  /**
   * Elastic query for the latest executions of the given schedules.
   * `null` when there is no schedule to look up.
   */
  static toSearchSummariesQuery = ({
    orgId,
    divsIds,
    scheduleIds,
    pageSize = SUMMARY_PAGE_SIZE,
  }: ExecutionSearchSummariesParams): ElasticQueryParams | null => {
    if (!scheduleIds.length) return null;

    return {
      entity: FSCollections.EXECUTIONS,
      organizationId: orgId,
      divisionIds: divsIds,
      filters: { terms: { 'schedule.id': scheduleIds } },
      pagination: { from: 0, size: pageSize },
      sort: [{ field: 'createdAt._seconds', order: 'desc' }],
      fields: SUMMARY_FIELDS,
    };
  };

  static fromElasticSearchSummaryHit = (
    hit: ElasticSearchHit<ExecutionElasticSummarySource>,
  ): ExecutionSearchSummary | null => {
    const source = hit._source ?? {};
    const id = source.id ?? hit._id;
    const divisionId = source.division?.id;

    if (!id || !divisionId) return null;

    return {
      id,
      division: { id: divisionId, name: source.division?.name },
      scheduleName: source.schedule?.name?.trim() || id,
      startAtMs: DateUtil.getTimestampMs(source.startAt),
    };
  };

  static fromElasticSearchSummariesResponse = (
    response: ElasticSearchResponse<ExecutionElasticSummarySource> | null,
  ): ExecutionSearchSummary[] =>
    (response?.hits?.hits ?? [])
      .map(hit => ExecutionMapper.fromElasticSearchSummaryHit(hit))
      .filter((item): item is ExecutionSearchSummary => item != null);

  /**
   * Id window for the real-time work-order list. `execution_records` already
   * carries `currentState`; Elastic decides which task records match and in
   * what order (`urgency` is a server-side script sort), then Firestore
   * materializes the live execution page.
   */
  static toSearchIdsQuery = ({
    orgId,
    divsIds,
    pageSize = 10,
    from = 0,
    searchTerm = '',
    memberUserIds = [],
    templateIds = [],
    statuses = [],
    executionId,
    sortField = 'urgency',
    sortOrder = 'asc',
    endedAfterMs,
  }: ExecutionSearchIdsParams): ElasticQueryParams => {
    const filters: ElasticQueryParams['filters'] = {
      term: { scheduleType: SCHEDULE_TYPES.task },
    };

    if (executionId) {
      filters.term = { ...filters.term, id: executionId };
    }

    const textSearch = searchTerm ? buildPartialMatch(['scheduleName'], searchTerm) : '';
    if (textSearch) filters.textSearch = textSearch;

    if (templateIds.length) {
      filters.terms = { ...filters.terms, scheduleTaskSummaryId: templateIds };
    }

    if (statuses.length) {
      filters.terms = { ...filters.terms, currentState: statuses };
    }

    if (memberUserIds.length) {
      filters.terms = { ...filters.terms, 'usersWithAccess.keyword': memberUserIds };
    }

    // Realtime window: keep tasks whose endAt is still within the last N hours
    // (includes long-running work that started earlier). `endAt._seconds` is a
    // date field in ES (services urgency script uses `.toInstant()`), so bounds
    // are unix-second strings with `format: epoch_second` — bare numbers would
    // be read as epoch millis.
    if (typeof endedAfterMs === 'number' && Number.isFinite(endedAfterMs)) {
      filters.range = {
        'endAt._seconds': {
          gt: String(Math.floor(endedAfterMs / 1000)),
          format: 'epoch_second',
        },
      };
    }

    return {
      entity: FSCollections.EXECUTION_RECORDS,
      organizationId: orgId,
      divisionIds: divsIds,
      filters,
      pagination: { from, size: pageSize },
      sort: [{ field: sortField, order: sortOrder }],
      fields: ID_FIELDS,
    };
  };

  static fromElasticSearchIdHit = (
    hit: ElasticSearchHit<ExecutionElasticIdSource>,
  ): ExecutionSearchIdHit | null => {
    const id = hit._source?.id ?? hit._id;
    const divisionId = hit._source?.divisionId;

    if (!id || !divisionId) return null;

    return { id, divisionId };
  };

  static fromElasticSearchIdsResponse = (
    response: ElasticSearchResponse<ExecutionElasticIdSource> | null,
  ): ExecutionSearchIdsResult => {
    const hits = response?.hits?.hits ?? [];
    const items = hits
      .map(hit => ExecutionMapper.fromElasticSearchIdHit(hit))
      .filter((item): item is ExecutionSearchIdHit => item != null);

    return { items, total: response?.hits?.total?.value ?? items.length };
  };
}
